class RealLessonComparison extends HTMLElement {
  connectedCallback() {
    this.onPointerOver = this.onPointerOver.bind(this);
    this.onPointerOut = this.onPointerOut.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);
    this.onFloatingCompareClick = this.onFloatingCompareClick.bind(this);
    this.onSectionChange = this.onSectionChange.bind(this);
    this.onDisciplineFilterChange = this.onDisciplineFilterChange.bind(this);

    this.table = this.querySelector('.real-lesson-comparison__table');
    if (!this.table) return;

    this.cells = Array.from(this.table.querySelectorAll('[data-real-lesson-comparison-column]'));
    this.floatingButton = this.querySelector('[data-real-lesson-comparison-floating]');
    this.pointerColumn = '';
    this.focusColumn = '';
    this.editorColumn = '';

    this.table.addEventListener('pointerover', this.onPointerOver);
    this.table.addEventListener('pointerout', this.onPointerOut);
    this.table.addEventListener('focusin', this.onFocusIn);
    this.table.addEventListener('focusout', this.onFocusOut);
    this.table.addEventListener('click', this.onClick);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    document.addEventListener('real:lesson-discipline-change', this.onDisciplineFilterChange);
    document.addEventListener('shopify:section:load', this.onSectionChange);
    document.addEventListener('shopify:section:unload', this.onSectionChange);
    document.addEventListener('shopify:section:reorder', this.onSectionChange);

    this.columnAnchors = new Map(
      this.cells
        .filter((cell) => cell.tagName === 'TH')
        .map((header) => {
          const anchor = header
            .querySelector('.real-lesson-comparison__view')
            ?.getAttribute('href')
            ?.replace(/^#/, '');

          return [header.dataset.realLessonComparisonColumn, anchor || ''];
        })
    );
    this.syncDisciplineFilter();

    if (this.floatingButton) {
      this.floatingButton.addEventListener('click', this.onFloatingCompareClick);
      this.setupFloatingCompare();
    }
  }

  disconnectedCallback() {
    this.table?.removeEventListener('pointerover', this.onPointerOver);
    this.table?.removeEventListener('pointerout', this.onPointerOut);
    this.table?.removeEventListener('focusin', this.onFocusIn);
    this.table?.removeEventListener('focusout', this.onFocusOut);
    this.table?.removeEventListener('click', this.onClick);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    document.removeEventListener('real:lesson-discipline-change', this.onDisciplineFilterChange);
    this.floatingButton?.removeEventListener('click', this.onFloatingCompareClick);
    document.removeEventListener('shopify:section:load', this.onSectionChange);
    document.removeEventListener('shopify:section:unload', this.onSectionChange);
    document.removeEventListener('shopify:section:reorder', this.onSectionChange);
    this.floatingStartObserver?.disconnect();
    this.floatingTargetObserver?.disconnect();
    if (this.sectionChangeFrame) cancelAnimationFrame(this.sectionChangeFrame);
    this.cells?.forEach((cell) => cell.classList.remove('is-active-column'));
  }

  setupFloatingCompare() {
    if (!this.floatingButton || !this.table) return;

    this.floatingStartObserver?.disconnect();
    this.floatingTargetObserver?.disconnect();

    this.setupFloatingCompareTarget();

    this.floatingStartTarget =
      document.querySelector('.real-lesson-hero__heading') ??
      document.querySelector('.real-lesson-hero__ctas') ??
      document.querySelector('.real-lesson-hero');

    if (!this.floatingStartTarget || !('IntersectionObserver' in window)) {
      this.floatingStartPassed = false;
      this.floatingTargetAhead = false;
      this.updateFloatingCompare();
      return;
    }

    const startRect = this.floatingStartTarget.getBoundingClientRect();
    const targetRect = this.getBoundingClientRect();

    this.floatingStartPassed = startRect.bottom <= 0;
    this.floatingTargetAhead = targetRect.top > window.innerHeight;
    this.updateFloatingCompare();

    this.floatingStartObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      const viewportTop = entry.rootBounds?.top ?? 0;
      this.floatingStartPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= viewportTop;
      this.updateFloatingCompare();
    });

    this.floatingTargetObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      const viewportBottom = entry.rootBounds?.bottom ?? window.innerHeight;
      this.floatingTargetAhead = !entry.isIntersecting && entry.boundingClientRect.top >= viewportBottom;
      this.updateFloatingCompare();
    });

    this.floatingStartObserver.observe(this.floatingStartTarget);
    this.floatingTargetObserver.observe(this);
  }

  setupFloatingCompareTarget() {
    if (!this.floatingButton) return;

    const comparisonSection = this.closest('.shopify-section');
    const precedingSection = comparisonSection?.previousElementSibling;
    const standaloneHeader = precedingSection?.matches('.real-lesson-section-header-section')
      ? precedingSection.querySelector('.real-lesson-section-header[id]')
      : null;
    const fallbackHref =
      this.floatingButton.dataset.realLessonComparisonFallbackHref ||
      this.floatingButton.getAttribute('href');

    if (standaloneHeader?.id) {
      this.floatingButton.setAttribute('href', `#${standaloneHeader.id}`);
    } else if (fallbackHref) {
      this.floatingButton.setAttribute('href', fallbackHref);
    }
  }

  updateFloatingCompare() {
    if (!this.floatingButton) return;

    const ownerButton = document.querySelector('[data-real-lesson-comparison-floating]');
    const owner = ownerButton?.closest('real-lesson-comparison');
    const shouldShow =
      owner === this && Boolean(this.floatingStartPassed) && Boolean(this.floatingTargetAhead);

    if (this.floatingButton.dataset.stuck === String(shouldShow)) return;

    this.floatingButton.dataset.stuck = String(shouldShow);
    this.floatingButton.setAttribute('aria-hidden', String(!shouldShow));
    this.floatingButton.toggleAttribute('inert', !shouldShow);
    this.floatingButton.tabIndex = shouldShow ? 0 : -1;
  }

  onFloatingCompareClick(event) {
    if (event.detail !== 0) return;

    const comparisonViewport = this.querySelector('.real-lesson-comparison__scroll');

    requestAnimationFrame(() => {
      comparisonViewport?.focus({ preventScroll: true });
    });
  }

  onSectionChange() {
    if (this.sectionChangeFrame) cancelAnimationFrame(this.sectionChangeFrame);

    this.sectionChangeFrame = requestAnimationFrame(() => {
      this.sectionChangeFrame = null;
      if (!this.isConnected) return;

      this.setupFloatingCompare();
      this.syncDisciplineFilter();
    });
  }

  onDisciplineFilterChange(event) {
    const programs = event.target;
    if (!(programs instanceof Element) || !programs.matches('real-lesson-programs')) return;
    if (!this.matchesPrograms(programs)) return;

    this.programsComponent = programs;
    this.applyDisciplineFilter(event.detail);
  }

  syncDisciplineFilter() {
    const programs = Array.from(document.querySelectorAll('real-lesson-programs')).find((candidate) =>
      this.matchesPrograms(candidate)
    );
    if (!programs) {
      this.programsComponent = null;
      this.applyDisciplineFilter();
      return;
    }

    this.programsComponent = programs;
    this.applyDisciplineFilter({
      disciplineKey: programs.selectedDiscipline,
      visibleAnchors: Array.from(programs.querySelectorAll('[data-real-lesson-card]'))
        .filter((card) => !card.hidden)
        .map((card) => card.id)
        .filter(Boolean),
    });
  }

  matchesPrograms(programs) {
    if (!(programs instanceof Element) || this.columnAnchors.size === 0) return false;

    const cardAnchors = new Set(
      Array.from(programs.querySelectorAll('[data-real-lesson-card]'))
        .map((card) => card.id)
        .filter(Boolean)
    );

    return Array.from(this.columnAnchors.values()).some((anchor) => cardAnchors.has(anchor));
  }

  applyDisciplineFilter({ disciplineKey, visibleAnchors = [] } = {}) {
    const isFiltered = Boolean(disciplineKey);
    const visible = new Set(visibleAnchors);
    let visibleColumnCount = 0;

    this.columnAnchors.forEach((anchor, column) => {
      const showColumn = !isFiltered || visible.has(anchor);
      if (showColumn) visibleColumnCount += 1;

      this.cells
        .filter((cell) => cell.dataset.realLessonComparisonColumn === column)
        .forEach((cell) => {
          cell.hidden = !showColumn;
        });
    });

    this.toggleAttribute('data-discipline-filtered', isFiltered);

    const comparison = this.querySelector('.real-lesson-comparison');

    if (comparison) {
      if (isFiltered) {
        const minimumWidth = Math.max(32, 10 + visibleColumnCount * 9.6);
        comparison.style.setProperty(
          '--real-lesson-comparison-table-min-width',
          `${minimumWidth}rem`
        );
      } else {
        comparison.style.removeProperty('--real-lesson-comparison-table-min-width');
      }
    }

    if (
      this.pointerColumn &&
      this.cells.find(
        (cell) =>
          cell.dataset.realLessonComparisonColumn === this.pointerColumn && !cell.hidden
      ) === undefined
    ) {
      this.pointerColumn = '';
    }

    if (
      this.focusColumn &&
      this.cells.find(
        (cell) =>
          cell.dataset.realLessonComparisonColumn === this.focusColumn && !cell.hidden
      ) === undefined
    ) {
      this.focusColumn = '';
    }

    this.updateColumnState();
  }

  getColumn(target) {
    if (!(target instanceof Element) || !this.table?.contains(target)) return '';

    return target.closest('[data-real-lesson-comparison-column]')?.dataset.realLessonComparisonColumn || '';
  }

  onPointerOver(event) {
    const column = this.getColumn(event.target);
    if (column === this.pointerColumn) return;

    this.pointerColumn = column;
    this.updateColumnState();
  }

  onPointerOut(event) {
    const column = this.getColumn(event.relatedTarget);
    if (column === this.pointerColumn) return;

    this.pointerColumn = column;
    this.updateColumnState();
  }

  onFocusIn(event) {
    const column = this.getColumn(event.target);
    if (column === this.focusColumn) return;

    this.focusColumn = column;
    this.updateColumnState();
  }

  onFocusOut(event) {
    const column = this.getColumn(event.relatedTarget);
    if (column === this.focusColumn) return;

    this.focusColumn = column;
    this.updateColumnState();
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('a, button, input, select, textarea, [role="button"]')) return;

    const column = this.getColumn(event.target);
    if (!column) return;

    this.getColumnHeader(column)?.querySelector('.real-lesson-comparison__view')?.click();
  }

  onBlockSelect(event) {
    const blockId = event.detail?.blockId;
    const header = this.querySelector(`[data-real-lesson-comparison-block-id="${blockId}"]`);

    if (header) {
      if (header.hidden && typeof this.programsComponent?.clearDiscipline === 'function') {
        this.programsComponent.clearDiscipline({ announce: false, focus: false });
      }

      this.editorColumn = blockId;
      this.updateColumnState();
      this.revealItem(header);
      return;
    }

    const pathItem = this.querySelector(
      `[data-real-lesson-comparison-path-block-id="${blockId}"], ` +
        `[data-real-lesson-comparison-gantt-block-id="${blockId}"]`
    );
    if (!pathItem) return;

    this.editorColumn = '';
    this.updateColumnState();
    this.revealItem(pathItem);
  }

  onBlockDeselect(event) {
    if (event.detail?.blockId && event.detail.blockId !== this.editorColumn) return;

    this.editorColumn = '';
    this.updateColumnState();
  }

  getColumnHeader(column) {
    return this.cells?.find(
      (cell) => cell.tagName === 'TH' && cell.dataset.realLessonComparisonColumn === column
    );
  }

  revealItem(target) {
    const viewport = this.querySelector('.real-lesson-comparison__scroll');
    if (!viewport) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const viewportRect = viewport.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const gutter = 24;
    const availableWidth = viewport.clientWidth - gutter * 2;
    const left =
      targetRect.width >= availableWidth
        ? viewport.scrollLeft + targetRect.left - viewportRect.left - gutter
        : viewport.scrollLeft +
          targetRect.left -
          viewportRect.left -
          (viewport.clientWidth - targetRect.width) / 2;

    viewport.scrollTo({
      left: Math.max(0, left),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
    this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
  }

  updateColumnState() {
    const activeColumn = this.editorColumn || this.focusColumn || this.pointerColumn;

    this.cells?.forEach((cell) => {
      cell.classList.toggle(
        'is-active-column',
        Boolean(activeColumn) && cell.dataset.realLessonComparisonColumn === activeColumn
      );
    });
  }
}

if (!customElements.get('real-lesson-comparison')) {
  customElements.define('real-lesson-comparison', RealLessonComparison);
}
